import Swal from 'sweetalert2';
import {inferUnitTestType, runUnitTests} from './runUnitTests';
import {
  UnitTestResults,
  allTestsPassed,
  firstTestHasError,
} from 'assignments/unitTest/unitTestResults';
import {checkIsProjectConsole, isCreativeProject} from 'ide/utils/general';
import {doc, getFirestore, runTransaction, setDoc} from 'firebase/firestore';
import {
  areAllMilestonesEarned,
  buildMilestoneProgressKey,
  type AssignmentMilestone,
} from '@cip/core/assignments';
import {
  AUTOGRADER_RUN_TOAST,
  logAiAutograderUserFeedback,
  AI_AUTOGRADER_NOT_CORRECT_SWAL,
  STUDENT_DISAGREE_SWAL,
} from '../../assignments/unitTest/ai/GptAutograderUtils';
import {CourseId, UserId} from 'types/common';
import {IDEContextData} from 'ide/contexts/IDEContext';
import {NavigateFunction} from 'react-router-dom';
import {AssignmentId, AssignmentUnitTestType} from 'assignments/types';
import {UNIT_TEST_TYPE_TO_SCHEMA} from 'assignments/unitTest/UnitTestSchemaRegistry';
import { AdvancedConsoleTestResult} from 'assignments/unitTest/advancedConsole/advancedConsoleUtils';
import type { TranslationFunction } from 'components/translation/utils';

/*
 * function: runUnitTestsAndReportResults
 * --------------------------------------
 * This is the main entry point for anyone running unit tests. This function
 * combines running the tests and reporting the results, both to the user and
 * to the server. It is async so that when using AI autograders, we can keep
 * the UI responsive.
 *
 * This function is only called in two places: From the IDE and from the TestView
 */

interface UnitTestParams {
  /** The context of the IDE. */
  ideContext: IDEContextData;
  /** The function to navigate to a new URL. */
  userId: UserId;
  /** The ID of the course the user is in. */
  courseId: CourseId;
  /** The function to navigate to a new URL. */
  onCorrectCallback: (unitTestResults: UnitTestResults) => void;
  /** The function to navigate to a new URL. */
  onIncorrectCallback: (unitTestResults: UnitTestResults) => void;
  /** Whether to run the tests silently (no UI feedback). */
  silent?: boolean;
}


/*
 * function: markAssnCorrect
 * -------------------------
 * Mark the assignment as correct in the database.
 */
export const markAssnCorrect = async (
  userId: UserId,
  courseId: CourseId,
  assnId: AssignmentId,
) => {
  const db = getFirestore();
  const firebasePath = `/users/${userId}/${courseId}/assnProgress`;
  const newCompletion = {};
  newCompletion[assnId] = true;
  const completeDoc = doc(db, firebasePath);
  await setDoc(completeDoc, newCompletion, {merge: true});
};

/**
 * Banks one milestone, and marks the assignment itself complete once that was
 * the last outstanding one. Both keys live in the same per-user `assnProgress`
 * document, so the certificate, the staff progress tables and the roadmap dot
 * keep reading whole-assignment completion with no knowledge of milestones.
 *
 * The write is `merge` and only ever sets `true`, so a milestone is never
 * un-earned and concurrent checks cannot clobber each other. Deciding the bare
 * key is a read-modify-write, so it runs in a transaction: two tabs banking the
 * last two milestones at once would otherwise each miss the other's write and
 * leave the assignment unfinished, with no later check to repair it.
 *
 * @param args - Who finished what.
 * @param args.userId - The student.
 * @param args.courseId - Course whose progress document is written.
 * @param args.assnId - Assignment the milestone belongs to.
 * @param args.milestoneId - Milestone just passed.
 * @param args.milestones - The assignment's full milestone list. An empty list
 *   writes only the milestone key, leaving whole-assignment completion to the
 *   ordinary path.
 * @return Resolves once the merge write completes.
 * @throws Re-raises Firestore errors from the read or the write.
 */
export const markMilestoneCorrect = async ({
  userId,
  courseId,
  assnId,
  milestoneId,
  milestones,
}: {
  userId: UserId;
  courseId: CourseId;
  assnId: AssignmentId;
  milestoneId: string;
  milestones: AssignmentMilestone[];
}) => {
  const db = getFirestore();
  const completeDoc = doc(db, `/users/${userId}/${courseId}/assnProgress`);
  const milestoneKey = buildMilestoneProgressKey({
    assignmentId: assnId,
    milestoneId,
  });

  await runTransaction(getFirestore(), async transaction => {
    const existing = await transaction.get(completeDoc);
    const progressAfterThisWrite = {
      ...(existing.exists() ? existing.data() : {}),
      [milestoneKey]: true,
    };
    const completesAssignment = areAllMilestonesEarned({
      milestones,
      progress: progressAfterThisWrite,
      assignmentId: assnId,
    });

    transaction.set(
      completeDoc,
      {
        [milestoneKey]: true,
        ...(completesAssignment && {[assnId]: true}),
      },
      {merge: true},
    );
  });
};



export async function runUnitTestsAndReportResults(unitTestParams: UnitTestParams) {
  const {
    ideContext,
    userId,
    courseId,
    onCorrectCallback,
    onIncorrectCallback,
  } = unitTestParams;
  if (isCreativeProject(ideContext.projectData)) {
    return;
  }
  const silent = unitTestParams.silent ?? false;
  const assnId = ideContext.assnData?.metaData.uid;
  const unitTestType = inferUnitTestType(
    ideContext.assnData,
    ideContext.projectData,
  );
  const shouldShowAutograderToast =
    !silent && (unitTestType === 'ai' || unitTestType === 'advanced_console');

  ideContext.setAutograderRunning(true);
  let autograderToastOpen = false;
  if (shouldShowAutograderToast) {
    AUTOGRADER_RUN_TOAST.fire();
    autograderToastOpen = true;
  }

  try {
    const unitTestResults = await runUnitTests({...ideContext, silent});

    if (autograderToastOpen) {
      AUTOGRADER_RUN_TOAST.close();
      autograderToastOpen = false;
    }

    ideContext.setUnitTestResults(unitTestResults);
    if (unitTestResults.testsRun) {
      updateTestCenterView(ideContext, unitTestResults);
      const passedAll = allTestsPassed(unitTestResults);
      if (passedAll) {
        // The run only covered the selected milestone, so banking the whole
        // assignment here would credit steps that were never checked.
        const milestones = ideContext.assignmentMilestones ?? [];
        const selectedMilestoneId = ideContext.selectedMilestoneId;
        if (milestones.length > 0 && selectedMilestoneId) {
          await markMilestoneCorrect({
            userId,
            courseId,
            assnId,
            milestoneId: selectedMilestoneId,
            milestones,
          });
        } else {
          await markAssnCorrect(userId, courseId, assnId);
        }
        if (!silent) {
          onCorrectCallback(unitTestResults);
        }
      } else if (!firstTestHasError(unitTestResults) && !silent) {
        onIncorrectCallback(unitTestResults);
      }
    }

    return unitTestResults;
  } finally {
    if (autograderToastOpen) {
      AUTOGRADER_RUN_TOAST.close();
    }
    ideContext.setAutograderRunning(false);
  }
}




// /****************************************************************************
//  *                      Helper Functions                                    *
//  ****************************************************************************/



export function reportFailedUnitTests(
  results: UnitTestResults,
  navigate: NavigateFunction,
  userId: UserId,
  courseId: CourseId,
  assnId: AssignmentId,
  onwardsUrl: string,
  unitTestType: AssignmentUnitTestType,
  t: TranslationFunction,
) {
  if (unitTestType === 'ai') {
    AI_AUTOGRADER_NOT_CORRECT_SWAL.fire().then(result => {
      if (result.isDenied) {
        markAssnCorrect(userId, courseId, assnId);

        // record a disagreement (if GPT grader)
        logAiAutograderUserFeedback({results, userId, courseId, assnId});

        STUDENT_DISAGREE_SWAL.fire({
          preConfirm: () => {
            navigate(onwardsUrl);
          },
        });
      }
    });
    return;
  }
  const failureMessage =
    UNIT_TEST_TYPE_TO_SCHEMA[unitTestType].getFailureMessage(results, t);
  Swal.fire({
    text: failureMessage,
    icon: 'info',
    confirmButtonText: t('Keep it up!') as string,
  });
}



function updateTestCenterView(
  ideContext: IDEContextData,
  results: UnitTestResults,
) {
  const {setConsoleTestResults} = ideContext;
  const {setAdvancedConsoleTestResults} = ideContext;
  
  const isConsole = checkIsProjectConsole(
    ideContext.projectData,
    ideContext.assnData,
  );
  
  const isAdvancedConsole = ideContext?.assnData?.metaData?.unitTestType === 'advanced_console';
  
  if (!isConsole && !isAdvancedConsole) {
    return;
  }

  const testResults = results.testResults;

  if (isAdvancedConsole) {
    setAdvancedConsoleTestResults(testResults as AdvancedConsoleTestResult[]);
  } else if (isConsole) {
    const rawResults = [];
    for (const result of testResults) {
      const rawResult = result.rawResult;
      rawResults.push({
        ...rawResult,
        state: result.isSuccess ? 'success' : 'failure',
      });
    }
    setConsoleTestResults(rawResults);
  }
}
